import DmsCoordinates from "dms-conversion";
import numeral from "numeral";

export function createGradientBase64(colors: string[], rotation: number = 0) {
  if (typeof document === "undefined") {
    return ''
  }
  const pass = colors && Array.isArray(colors) && colors.length >= 1
  if (!pass) {
    throw new Error("At least one colour are required to create a gradient.");
  }

  // Create a canvas element
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (ctx === null) return ''
  // Set canvas dimensions
  canvas.width = 100;  // Width of the gradient image
  canvas.height = 1;   // Height of the gradient image

  // Create a linear gradient
  const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
  let useColors = [...colors]
  if (useColors.length === 1) {
    useColors = [...useColors, '#aad3e0']
  }
  // Add colors to the gradient
  const step = 1 / (useColors.length - 1);
  useColors.forEach((color, index) => {
    gradient.addColorStop(step * index, color);
  });

  // Fill the canvas with the gradient
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  if (rotation !== 0) {

    const originalWidth = canvas.width;
    const originalHeight = canvas.height;
  
    // Create a temporary canvas to store the original content
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) {
      console.error('Failed to get 2D context for temporary canvas');
      return '';
    }
  
    tempCanvas.width = originalWidth;
    tempCanvas.height = originalHeight;
  
    // Copy the original canvas content to the temporary canvas
    tempCtx.drawImage(canvas, 0, 0);
  
    // Rotate the canvas by swapping width and height
    canvas.width = originalHeight;
    canvas.height = originalWidth;
  
    // Rotate the context and draw the image
    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2); // Move origin to center
    ctx.rotate((rotation * Math.PI) / 180); // Rotate by 90 degrees
    ctx.drawImage(tempCanvas, -originalWidth / 2, -originalHeight / 2);
    ctx.restore();  
  }
  
  // Convert the canvas to a base64-encoded image string
  return canvas.toDataURL("image/png");
}


export function drawGeoJsonOnCanvas(
  canvas: HTMLCanvasElement,
  geojson: any,
  options?: {
    onFeatureDrawn?: (properties: any) => {
      line: string;
      fill: string;
    }; // Callback after each feature is drawn
    lineColor?: string; // Colour for line strings and polygon borders
    fillColor?: string; // Fill colour for polygons
    bbox?: [number, number, number, number]; // Bounding box [minX, minY, maxX, maxY]
  }
) {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    console.error('Failed to get 2D context for canvas');
    return;
  }

  ctx.lineWidth = 5;

  // Default options
  const {
    lineColor = '#ffffff',
    fillColor = 'rgba(255, 255, 255, 0.2)', // Semi-transparent fill
    bbox = [-180, -90, 180, 90], // Default to global map extent
  } = options || {};

  // Extract bounding box dimensions
  const [minX, minY, maxX, maxY] = bbox;
  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;

  // Coordinate-to-pixel transform function
  const coordToPixel = (lon: number, lat: number): [number, number] => {
    const x = ((lon - minX) / (maxX - minX)) * canvasWidth;
    const y = ((maxY - lat) / (maxY - minY)) * canvasHeight; // Invert Y-axis
    return [x, y];
  };

  // Helper function to draw polygons
  const drawPolygon = (coordinates: number[][][], color: string, fillStyle: string) => {
    ctx.beginPath();
    coordinates.forEach((ring) => {
      ring.forEach(([lon, lat], index) => {
        const [x, y] = coordToPixel(lon, lat);
        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      ctx.closePath(); // Close the ring
    });
    ctx.fillStyle = fillStyle;
    ctx.fill();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.stroke();
  };

  const drawLineString = (coordinates: number[][], color: string) => {
    ctx.beginPath();
    coordinates.forEach(([lon, lat], index) => {
      const [x, y] = coordToPixel(lon, lat);
      if (index === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.strokeStyle = color;
    ctx.stroke();
  };

  const drawPoint = (
    [lon, lat]: number[],
    r: number = 5,
    color: string
  ): void => {
    ctx.beginPath();
    const [x, y] = coordToPixel(lon, lat);
    ctx.arc(x, y, r, 0, Math.PI * 2); // Draw full circle
    ctx.fillStyle = color;
    ctx.fill();
    ctx.lineWidth = 5;
    ctx.strokeStyle = color;
    ctx.stroke();
  }
  

  // Iterate through GeoJSON features
  const features = geojson.features || [];

  features.forEach((feature: any) => {
    const { properties, geometry } = feature;
    const { type, coordinates } = geometry;
    const { color} = properties || {}
    let useColor = color || lineColor
    let styleColor = fillColor
    if (options?.onFeatureDrawn) {
      const { line, fill } = options.onFeatureDrawn(properties)
      useColor = line
      styleColor = fill
    }
    switch (type) {
      case 'Point':
        drawPoint(coordinates, 5, useColor );
        break;
      case 'Polygon':
        drawPolygon(coordinates, useColor, styleColor);
        break;
      case 'MultiPolygon':
        coordinates.forEach((polygon: number[][][]) => drawPolygon(polygon, useColor, styleColor));
        break;
      case 'LineString':
        drawLineString(coordinates, useColor);
        break;
      case 'MultiLineString':
        coordinates.forEach((line: number[][]) => drawLineString(line, useColor));
        break;
      default:
        console.warn(`Unsupported geometry type: ${type}`);
    }
  });
}

export async function addCredits(
  canvas: HTMLCanvasElement,
  credits: string[],
): Promise<void> {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get 2D context for canvas');
  }

  const fontSize = 17
  ctx.font = `normal ${fontSize}px Arial`;
  ctx.fillStyle = 'black';
  ctx.textAlign = "right";
  ctx.fillText(credits.join(', ').replace(/(&copy;)/gi, `©`).replace(/<(?:"[^"]*"['"]*|'[^']*'['"]*|[^'">])+>/gi, ''), canvas.width - 24, canvas.height - 30);
}

export async function drawImageOnCanvasOnly(
  canvas: HTMLCanvasElement,
  imageUrl: string,
  x: number,
  y: number,
  width: number,
  height: number
): Promise<HTMLCanvasElement> {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get 2D context for canvas');
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous"; // optional, useful for external URLs

    img.onload = () => {
      ctx.drawImage(img, x, y, width, height);
      resolve(ctx.canvas);
    };

    img.onerror = (err) => reject(err);
    img.src = imageUrl;
  });
}



export async function drawImageOnCanvas(
  canvas: HTMLCanvasElement,
  base64Data: string,
  width: number,
  height: number,
  title: string,
  minValue: number | null = 0,
  maxValue: number | null = 0,
  reverse: boolean = false,
  valueFormat: string
): Promise<void> {
  const topOffset = 126
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get 2D context for canvas');
  }

  // Create a new Image object
  const image = new Image();

  // Set the source of the image to the base64 data
  image.src = base64Data;

  // Wait for the image to load using a Promise
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('Failed to load the image from the provided base64 data'));
  });

  ctx.fillStyle = '#000';
  ctx.fillRect(canvas.width - 98 - 120, topOffset, width+4, height+4);
  ctx.drawImage(image, canvas.width - 96 - 120, topOffset + 2, width, height);

  ctx.font = '18px Arial';
  ctx.fillStyle = 'black';
  ctx.textAlign = 'left';

  if (maxValue !== null && minValue !== null) {
    const range = Math.abs(maxValue - minValue)
    const steps = 4
    const sectionHeight = height / steps;
    const stepIncrease = range/steps
    const isNegative = minValue > maxValue
    const rangeValues = [...[...new Array(steps)].map((v, index) => {
      if (isNegative) {
        return minValue - stepIncrease*index  
      }
      return minValue + stepIncrease*index
    }), maxValue].reverse()

    const tickWidth = 2
    rangeValues.forEach((val, index) => {
      let top = (index * sectionHeight)
      if (reverse) {
        top = height - top
      }
      const isLast = (index === 0)
      const isFirst = (index === rangeValues.length - 1)
      let topPx = !isLast ? (topOffset  + tickWidth + top) : (topOffset)
      if (isFirst) {
        topPx = height + topOffset + 2
      }

      ctx.fillText(`${numeral(val).format(valueFormat)}`, canvas.width - 166, topPx + 6);
      ctx.fillRect(canvas.width - 192, topPx , 16, tickWidth);
    })
  }

  drawRotatedText(ctx, title, canvas.width-100, canvas.height-120, -90)
}

export function drawRotatedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  rotate: number, // Degrees
  font: string = "32px Arial",
  color: string = "black"
): void {
  ctx.save(); // Save the current state

  // Set font to measure text width & height
  ctx.font = font;
  
  // Get text metrics for proper alignment
  const textMetrics = ctx.measureText(text);

  // Convert degrees to radians
  const angle = (rotate * Math.PI) / 180;

  // Move to (x, y), rotate, then draw at (0, 0)
  ctx.translate(x, y);
  ctx.rotate(angle);

  // Apply styles
  ctx.fillStyle = color;
  ctx.textAlign = "left"; // Ensure text starts at translated (0,0)
  ctx.textBaseline = "top"; // Align text top to translated (0,0)

  // Draw text at the rotated origin
  ctx.fillText(text, 0, 0);

  ctx.restore(); // Restore original state
}


// @ts-ignore
function drawBorder(ctx, color1, color2, xPos, yPos, width, height, thickness = 2)
{
  ctx.fillStyle = color1;
  ctx.fillRect(xPos - (thickness), yPos - (thickness), width + (thickness * 2), height + (thickness * 2));
  ctx.fillStyle = color2;
  ctx.fillRect(xPos, yPos, width, height);
}

export async function createCanvasWithGraticuleBorder(
  canvas: HTMLCanvasElement,
  bbox: [number, number, number, number],
  base64Data: string
): Promise<HTMLCanvasElement> {
  // Extract canvas dimensions
  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;

  // Extract bounding box values (minLat, maxLat, minLon, maxLon)
  const [minLon, minLat, maxLon, maxLat] = bbox;

  // Create a new canvas for the final result
  const borderSize = 8; // Border width in pixels
  const margin = 120; // Space for labels
  const newCanvas = document.createElement('canvas');
  // create extra space for scale on the right side
  newCanvas.width = canvasWidth + 2 * (margin + borderSize) + margin;
  newCanvas.height = canvasHeight + 2 * (margin + borderSize);

  // Get 2D context for drawing
  const ctx = newCanvas.getContext('2d') as CanvasRenderingContext2D;
  
  // draw white background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, newCanvas.width, newCanvas.height);


  // Load and draw the base64 image
  const img = new Image();
  img.src = base64Data;

  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = (err) => reject(err);
  });

  // Draw the image onto the new canvas
  const imageX = margin + borderSize;
  const imageY = margin + borderSize;
  ctx.drawImage(img, imageX, imageY, canvasWidth, canvasHeight);

  // Function to draw alternating solid black/white sections
  function drawGraticuleBorder(sectionsLat: number, sectionsLon: number) {
    const sectionWidth = canvasWidth / sectionsLon;
    const sectionHeight = canvasHeight / sectionsLat;

    for (let i = 0; i < sectionsLat; i++) {
      const colorVertical = i % 2 === 0 ? "white" : "black";
      // Left border
      drawBorder(ctx, 'black', colorVertical, margin + borderSize, imageY + i * sectionHeight, borderSize, sectionHeight);
      // Right border
      drawBorder(ctx, 'black', colorVertical, margin + canvasWidth, imageY + i * sectionHeight, borderSize, sectionHeight);

    }
    for (let i = 0; i < sectionsLon; i++) {
      const colorHorizontal = i % 2 === 0 ? "black" : "white";
      // Top border
      drawBorder(ctx, 'black', colorHorizontal, imageX + i * sectionWidth, margin, sectionWidth, borderSize)
      // Bottom border
      drawBorder(ctx, 'black', colorHorizontal,imageX + i * sectionWidth, margin + borderSize + canvasHeight, sectionWidth, borderSize);
    }
  }

  const getSteps = (diff: number) => {
    if (diff >= 50) {
      return 10
    }
    if (diff >= 25) {
      return 5
    }
    if (diff >= 10) {
      return 2
    }
    if (diff >= 5) {
      return 1
    }
    if (diff >= 2) {
      return 0.5
    }
    return 0.25
  }

  // Function to draw tick labels at evenly spaced points
  function drawLabels() {
    const diffLat = Math.ceil(maxLat - minLat);
    const diffLon = Math.ceil(maxLon - minLon);

    const latSteps = Math.floor(diffLat/getSteps(diffLat))
    const lonSteps = Math.floor(diffLon/getSteps(diffLon))
    

    const intervalLat = (maxLat - minLat) / latSteps;
    const intervalLon = (maxLon - minLon) / lonSteps;
    // const minLatStart = getRoundedNum(minLat)
    // const minLonStart = getRoundedNum(minLon)
    
    const minLatStart = minLat
    const minLonStart = minLon
    
    ctx.font = '18px Arial';
    ctx.fillStyle = 'black';
    ctx.textAlign = 'center';

    for (let i = 0; i < 10; i++) {
      // const lat = minLatStart + i * Math.round(intervalLat*100)/100;
      const lat = minLatStart + i * intervalLat;
      const lon = minLonStart + i * intervalLon;

      const latPos = imageY + canvasHeight - (i * canvasHeight / latSteps) + 6;
      const lonPos = imageX + (i * canvasWidth / lonSteps);
      const dmsCoords = new DmsCoordinates(lat, lon);
      const { longitude, latitude } = dmsCoords.dmsArrays;

      const [lat_d, lat_m,, lat_cardinal] = latitude;
      const [lon_d, lon_m,, lon_cardinal] = longitude;
      
      if (lat <= maxLat) {
        // Left & Right Latitude Labels
        ctx.fillText(`${lat_d}°${lat_m}′ ${lat_cardinal}`, margin - 50, latPos);
        // ctx.fillText(toDegreesMinutes(lat), margin + canvasWidth + 2 * borderSize + 30, latPos);
      }

      if (lon <= maxLon) {
        // Top & Bottom Longitude Labels
        // ctx.fillText(toDegreesMinutes(lon), lonPos, margin - 10);
        ctx.fillText(`${lon_d}°${lon_m}′ ${lon_cardinal}`, lonPos, margin + canvasHeight + 2 * borderSize + 25);
      }
    }
    return [latSteps, lonSteps]
  }

  // Draw the alternating solid border and labels
  
  const [labelsLat, labelsLon] = drawLabels();
  drawGraticuleBorder(labelsLat, labelsLon);
  return newCanvas;
}


export function drawScaleLegend(
  zoom: number,
  canvas: HTMLCanvasElement,
  latitude: number,
): HTMLCanvasElement {
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;

  function getMetersPerPixel(zoom: number, latitude: number): number {
    return (156543.03392 * Math.cos((latitude * Math.PI) / 180)) / Math.pow(2, zoom);
  }

  const metersPerPixel = getMetersPerPixel(zoom, latitude);

  // Define target scale bar sizes (round to nice numbers)
  const targetScales = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000];
  function getClosestScale(value: number) {
    return targetScales.reduce((prev, curr) => (Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev));
  }

  // Calculate scale in meters for a 100px reference width
  const refWidthPx = 100;
  const scaleMeters = metersPerPixel * refWidthPx;

  // Round to the nearest nice number
  const scaleKm = getClosestScale(scaleMeters / 1000);

  // Calculate new bar widths
  const barKmWidth = (scaleKm * 1000) / metersPerPixel;

  // Legend position (bottom-left)
  const legendX = 140;
  const legendY = canvas.height - 60;

  ctx.fillStyle = "rgba(0, 0, 0, 1)";
  ctx.fillRect(legendX, legendY, barKmWidth, 2);
  // ctx.strokeStyle = "rgba(0, 0, 0, 1)";
  // ctx.lineWidth = 1;
  // ctx.strokeRect(legendX, legendY, barKmWidth-1, 30-1);
  ctx.fillRect(legendX, legendY-16, 2, 16);
  ctx.fillRect(legendX+barKmWidth-2, legendY-16, 2, 16);
  
  ctx.font = "16px Arial";
  ctx.textAlign = "center";
  ctx.fillStyle = "black";
  ctx.fillText(`${scaleKm} km`, legendX + barKmWidth/2, legendY - 10);

  // if (showMiles) {
  //   const scaleMiles = getClosestScale(scaleMeters / 1609.34);
  //   const barMiWidth = (scaleMiles * 1609.34) / metersPerPixel;
  
  //   // Draw miles scale bar below
  //   ctx.fillStyle = "rgba(255, 255, 255, 0.8)";
  //   ctx.fillRect(legendX, legendY + 30, barMiWidth, 30);
  //   ctx.strokeStyle = "rgba(0, 0, 0, 1)";
  //   ctx.lineWidth = 1;
  //   ctx.strokeRect(legendX, legendY+30, barMiWidth-1, 30-1);
  //   ctx.fillText(`${scaleMiles} mi`, legendX + 30, legendY + 50);
  // }


  return canvas 
}

const interpolateColor = (startColor: string, endColor: string, ratio: number): string => {
  const [r1, g1, b1] = startColor.match(/\d+/g)!.map(Number);
  const [r2, g2, b2] = endColor.match(/\d+/g)!.map(Number);
  const interpolate = (start: number, end: number) => Math.round(start + ratio * (end - start));
  return `rgb(${interpolate(r1, r2)}, ${interpolate(g1, g2)}, ${interpolate(b1, b2)})`;
};

export const createGradient = (hexColors: string[]): string[] => {
  const rgbColors = hexColors.map(hex => {
    const bigint = parseInt(hex.slice(1), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgb(${r}, ${g}, ${b})`;
  });

  const gradientColors: string[] = [];
  const steps = 16;
  for (let i = 0; i < steps; i++) {
    const ratio = i / (steps - 1);
    const segment = Math.floor(ratio * (rgbColors.length - 1));
    const startColor = rgbColors[segment];
    const endColor = rgbColors[segment + 1] || rgbColors[segment];
    const interpolatedColor = interpolateColor(startColor, endColor, ratio % 1);
    gradientColors.push(interpolatedColor);
  }

  return gradientColors;
};
